import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { lighten } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Link } from "react-router";
import backgroundWebp from "../../assets/GradientBackground.webp";
import ShuttlerockLogo from "../../assets/ShuttlerockLogo.svg";

// Space Black 900 from the brand guidelines, the same ink as the primary
// button on the Auth0 sign-in pages. The theme has no brand ink yet, so it is
// pinned here rather than read from the palette.
const BRAND_INK = "#121319";

// One of the arc artwork's band colours (the same red the Auth0 panel and the
// email banner fall back to), shown while the background image loads or if it
// fails, so the page never flashes the old blue.
const BRAND_RED = "#C91C4B";

export interface ErrorPageProps {
  title: string;
  body: string;
  logoRoute?: string;
  primaryButton?: {
    onClick?: () => void;
    text: string;
    route?: string;
  };
  secondaryButton?: {
    text: string;
    route: string;
  };
}

export const ErrorPage = ({
  body,
  logoRoute = "/",
  title,
  primaryButton,
  secondaryButton,
}: ErrorPageProps) => {
  const getRouteProps = (route: string) => {
    return route.startsWith("http")
      ? { component: "a" as const, href: route }
      : { component: Link, to: route };
  };

  return (
    <Stack
      // Force LightTheme as this page is used universally but uses light theme's colors.
      data-light="true"
      sx={{
        backgroundImage: `url(${backgroundWebp})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundColor: BRAND_RED,
        height: "100%",
      }}
    >
      <Stack
        sx={{ width: "100%", backgroundColor: ["grey.A50", "transparent"] }}
        alignItems={["center", "flex-start"]}
        pl={[null, 5]}
        py={[2, 3]}
      >
        <Box
          sx={{
            color: "text.primary",
            width: "240px",
            path: {
              fill: [null, "#fff"],
            },
          }}
          component={Link}
          to={logoRoute}
        >
          <ShuttlerockLogo data-testid="ShuttlerockLogo" style={{ display: "block" }} />
        </Box>
      </Stack>
      <Stack sx={{ flex: 1 }} justifyContent="center" alignItems="center">
        <Box sx={{ maxWidth: "650px" }} px={3}>
          <Typography
            variant="h1"
            color="common.white"
            mb={3}
            sx={{
              // oxlint-disable-next-line shuttlerock/no-typography-overrides -- Access Denied hero heading has no matching variant for its responsive 48px/80px size
              fontSize: ["3rem", "5rem"],
              // oxlint-disable-next-line shuttlerock/no-typography-overrides -- hero heading uses tight line-height distinct from any typography variant
              lineHeight: 1,
              // oxlint-disable-next-line shuttlerock/no-typography-overrides -- preserves the pre-existing heading weight carried over from the removed GlitchText wrapper
              fontWeight: 700,
            }}
          >
            {title}
          </Typography>
          <Typography variant="subtitle1" component="p" color="common.white" mb={9}>
            {body}
          </Typography>
          <Stack direction={["column", "row"]} sx={{ gap: 2 }}>
            {primaryButton ? (
              <Button
                variant="contained"
                onClick={primaryButton?.onClick}
                sx={{
                  backgroundColor: BRAND_INK,
                  color: "#fff",
                  // The sign-in pages lighten on hover by letting 20% of the
                  // white card through; this sits on artwork, so the same
                  // shade is mixed in as a solid colour instead.
                  "&:hover": { backgroundColor: lighten(BRAND_INK, 0.2) },
                }}
                {...(primaryButton?.route ? getRouteProps(primaryButton.route) : {})}
              >
                {primaryButton?.text}
              </Button>
            ) : null}
            {secondaryButton ? (
              <Button variant="contained" color="white" {...getRouteProps(secondaryButton?.route)}>
                {secondaryButton?.text}
              </Button>
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
};
